import { defineCustomElement, getAttribute, getBooleanAttribute, getLiteralAttribute, getRect, isAttrEqual, isAttrTrue, NectaryElement, setClass, updateAttribute, updateBooleanAttribute, updateLiteralAttribute } from '../utils';
import { DEFAULT_SIZE, sizeValues } from '../utils/size';
const templateHTML = '<style>:host{display:inline-flex;flex-direction:column;position:relative}#badge-wrapper{position:absolute;left:0;top:0;width:fit-content;border-radius:var(--sinch-comp-badge-shape-radius);padding:1px;pointer-events:none;background-color:var(--sinch-comp-badge-color-border)}#badge{box-sizing:border-box;color:var(--sinch-comp-badge-color-text);background-color:var(--sinch-comp-badge-color-background);width:20px;height:20px;border-radius:var(--sinch-comp-badge-shape-radius)}#badge.long{width:fit-content;padding:0 5px}#text{display:block;width:100%;height:100%;text-align:center;font:var(--sinch-comp-badge-font-size-l);line-height:20px;text-rendering:optimizelegibility}:host([size="m"]) #badge-wrapper{left:calc(100% - 8px);top:-8px}:host([size="m"]) #badge{width:14px;height:14px}:host([size="m"]) #badge.long{width:fit-content;padding:0 3px}:host([size="m"]) #text{font:var(--sinch-comp-badge-font-size-m);line-height:14px}:host([size="s"]) #badge{width:8px;height:8px;padding:0}:host([size="s"]) #text{display:none}:host([hidden]) #badge-wrapper{display:none}</style><slot id="slot"></slot><div id="badge-wrapper"><div id="badge"><span id="text"></span></div></div>';
import { modeValues } from './utils';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-badge', class extends NectaryElement {
  #$badgeWrapper;
  #$badge;
  #$text;
  #observer;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$badgeWrapper = shadowRoot.querySelector('#badge-wrapper');
    this.#$badge = shadowRoot.querySelector('#badge');
    this.#$text = shadowRoot.querySelector('#text');
    this.#observer = new ResizeObserver(this.#onResize);
  }
  connectedCallback() {
    super.connectedCallback();
    this.#observer.observe(this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.unobserve(this);
  }
  static get observedAttributes() {
    return ['text', 'size', 'mode', 'hidden'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    if (isAttrEqual(oldVal, newVal)) {
      return;
    }
    switch (name) {
      case 'text':
        {
          this.#$text.textContent = newVal;
          setClass(this.#$badge, 'long', newVal !== null && newVal.length > 1);
          break;
        }
      case 'size':
        {
          this.#updatePosition();
          break;
        }
      case 'mode':
        {
          this.#updatePosition();
          break;
        }
      case 'hidden':
        {
          this.#updatePosition();
          updateBooleanAttribute(this, name, isAttrTrue(newVal));
          break;
        }
    }
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get size() {
    return getLiteralAttribute(this, sizeValues, 'size', DEFAULT_SIZE);
  }
  set size(value) {
    updateLiteralAttribute(this, sizeValues, 'size', value);
  }
  get mode() {
    return getLiteralAttribute(this, modeValues, 'mode', 'square');
  }
  set mode(value) {
    updateLiteralAttribute(this, modeValues, 'mode', value);
  }
  get hidden() {
    return getBooleanAttribute(this, 'hidden');
  }
  set hidden(isHidden) {
    updateBooleanAttribute(this, 'hidden', isHidden);
  }
  get badgeRect() {
    if (this.hidden) {
      const selfRect = getRect(this);
      return {
        x: selfRect.x + selfRect.width,
        y: selfRect.y,
        width: 0,
        height: 0
      };
    }
    return getRect(this.#$badgeWrapper);
  }
  #updatePosition() {
    if (this.hidden) {
      return;
    }
    const mode = this.mode;
    const targetRect = this.getBoundingClientRect();
    const badgeRect = this.#$badgeWrapper.getBoundingClientRect();
    const offsetMultX = mode === 'circle' ? 0.85 : 1;
    const offsetMultY = mode === 'circle' ? 0.15 : 0;
    const posX = Math.round(targetRect.width * offsetMultX - badgeRect.height / 2);
    const posY = Math.round(targetRect.height * offsetMultY - badgeRect.height / 2);
    this.#$badgeWrapper.style.setProperty('left', `${posX}px`);
    this.#$badgeWrapper.style.setProperty('top', `${posY}px`);
  }
  #onResize = () => {
    this.#updatePosition();
  };
});